import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { articles } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";

const SITE_ORIGIN = (import.meta.env.VITE_SITE_URL || "https://basetaosheet.buzz").replace(/\/+$/, "");

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  const article = articles.find((a) => a.slug === slug);
  const lang = i18n.language?.split("-")[0] || "en";
  const content = article?.content[lang] || article?.content.en;



  if (!article || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 px-4 md:px-6 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            {t("articleNotFound")}
          </h1>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            {t("navHome")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: content.h1,
      description: content.metaDescription,
      image: article.image,
      author: { "@type": "Organization", name: t("authorName") },
      dateModified: new Date().toISOString().split("T")[0],
      inLanguage: lang,
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_ORIGIN}/article/${article.slug}` },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const sections = content.sections.map((s, i) => ({ ...s, id: `section-${i}` }));

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={jsonLd} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <nav className="text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                {t("navHome")}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-700">{content.h1}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {content.h1}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-semibold">
                  {t("authorName").charAt(0)}
                </span>
                <span className="font-medium text-foreground-800">{t("authorName")}</span>
              </span>
              <span className="text-foreground-300">•</span>
              <span>{t("authorRole")}</span>
              <span className="text-foreground-300">•</span>
              <span>{content.updatedDate}</span>
              <span className="text-foreground-300">•</span>
              <span>{content.readingTime}</span>
            </div>

            <p className="mt-8 text-base md:text-lg text-foreground-700 leading-relaxed border-l-4 border-primary-300 pl-4">
              {content.intro}
            </p>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="text-sm font-semibold text-foreground-950 mb-3">{t("tocTitle")}</h2>
              <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      <span className="text-xs font-semibold text-primary-600">{i + 1}.</span>
                      {s.heading}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#faq"
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    <span className="text-xs font-semibold text-primary-600">
                      {sections.length + 1}.
                    </span>
                    {t("articleFaqTitle")}
                  </a>
                </li>
              </ol>
            </div>

            {sections.map((s) => (
              <section key={s.id} id={s.id} className="mt-10 scroll-mt-24">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {s.heading}
                </h2>
                {s.paragraphs.map((p, i) => (
                  <p key={i} className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            ))}

            {/* CTA */}
            <div className="mt-12 rounded-lg bg-primary-100/70 p-6 md:p-8 text-center">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                {content.ctaTitle}
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">{content.ctaText}</p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("ctaBuyNow")}
                <i className="ri-arrow-right-up-line text-sm" />
              </a>
            </div>

            {/* FAQ */}
            <section id="faq" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                {t("articleFaqTitle")}
              </h2>
              <div className="mt-5 space-y-3">
                {content.faq.map((f) => (
                  <details
                    key={f.q}
                    className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between w-full px-5 py-4 text-left cursor-pointer list-none">
                      <span className="text-sm md:text-base font-medium text-foreground-900">
                        {f.q}
                      </span>
                      <span className="text-lg text-foreground-500 transition-transform group-open:rotate-180">
                        <i className="ri-arrow-down-s-line" />
                      </span>
                    </summary>
                    <div className="px-5 pb-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                      {f.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Related + back */}
            <div className="mt-12 pt-6 border-t border-background-200">
              <h2 className="text-sm font-semibold text-foreground-950">{t("relatedTitle")}</h2>
              <div className="mt-3 flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line" />
                  {t("backHome")}
                </Link>
                <Link
                  to="/#categories"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-price-tag-3-line" />
                  {t("navCategories")}
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}